import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { UPDATE_MEASUREMENT_TECHNOLOGY_MUTATION } from "../../../../graphql/mutations";

const EditMeasurementTechModal = ({ show, technology, currentTenant, handleClose, viewOnly, handleEnvChange, env }) => {
  const navigate = useNavigate();
  const [validationError, setValidationError] = useState("");

  const [formData, setFormData] = useState({
    id: technology?.id || "",
    name: technology?.name || "",
    description: technology?.description || ""
  });

  useEffect(() => {
    if (technology) {
      setFormData({
        id: technology.id,
        name: technology.name,
        description: technology.description
      });
      // reset validation error when form data changes
      setValidationError("");
    }
  }, [technology]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    if (name === "name" && validationError) {
      setValidationError("");
    }
  };

  const [updateMeasurementTechnology, { loading: mutationLoading }] = useMutation(UPDATE_MEASUREMENT_TECHNOLOGY_MUTATION);

  const validateForm = () => {
    // check if name contains only whitespace
    if (!formData.name.trim()) {
      setValidationError("Name cannot be empty or contain only whitespace");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // validate form before submitting
    if (!validateForm()) {
      return;
    }

    try {
      const { data } = await updateMeasurementTechnology({
        variables: {
          id: formData.id,
          measurementTechnology: {
            name: formData.name.trim(), // trim whitespace before submitting
            description: formData.description
          }
        }
      });

      if (data?.updateMeasurementTechnology) {
        handleClose();
        setTimeout(() => {
          toast.success(`Measurement Technology: ${data?.updateMeasurementTechnology.name} updated successfully.`);
          navigate("/measurement-tech");
          handleEnvChange(env, currentTenant);
        }, 200);
      }
    } catch (error) {
      handleClose();
      setTimeout(() => {
        toast.error("Failed to edit measurement technology.");
      }, 200);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton className="d-flex justify-content-center">
        <Modal.Title className="w-100 text-center">Update Measurement Technology</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit} noValidate>
          {/* Technology Name */}
          <Form.Group className="mb-3" controlId="formName">
            <Form.Label className="font-weight-bold">
              <strong>Name</strong>
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={viewOnly}
              isInvalid={!!validationError}
            />
            <Form.Control.Feedback type="invalid">{validationError}</Form.Control.Feedback>
          </Form.Group>

          {/* Technology Description */}
          <Form.Group className="mb-3" controlId="formDescription">
            <Form.Label className="font-weight-bold">
              <strong>Description</strong>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={8}
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              disabled={viewOnly}
              style={{ resize: "vertical" }}
            />
          </Form.Group>

          {!viewOnly && (
            <div className="d-grid">
              <Button variant="primary" type="submit" style={{ width: "50%" }} className="mx-auto" disabled={mutationLoading}>
                {mutationLoading ? "Updating measurement technology..." : "Update"}
              </Button>
            </div>
          )}
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export { EditMeasurementTechModal };
